/**
 * Taxonomy Builders
 *
 * Build taxonomy tree structures from owl2 Ontology for UI components.
 * These functions replace the ontology-loaders module.
 */

import React from 'react';
import type {
    Ontology,
    ClassExpression,
    ObjectPropertyExpression
} from '@semantical/modules/owl2';
import {
    parseTurtle,
    iriToString,
    getLocalName,
    isClass,
    isClassEntity,
    isObjectPropertyEntity,
    isDataPropertyEntity,
    isNamedIndividualEntity,
    isDeclaration,
    isSubClassOf,
    isClassAssertion,
    isAnnotationAssertion,
    isObjectPropertyDomain,
    isObjectPropertyRange,
    isSubObjectPropertyOf,
    isDataPropertyDomain,
    isDataPropertyRange,
    isFullIRI,
    isAbbreviatedIRI,
    isObjectProperty,
    isNamedIndividual,
    RDFS_NAMESPACE,
    fullIRI,
    owlClass,
    objectAllValuesFrom,
    isDataSomeValuesFrom,
    isDataAllValuesFrom,
    isDataMinCardinality,
    isDataMaxCardinality,
    isDataExactCardinality
} from '@semantical/modules/owl2';

import SchemaIndividual from './SchemaIndividual.ts';

import type {
    ClassTaxonomyNode,
    RelationshipTaxonomyNode,
    ClassHierarchyMap,
    DataProperty,
    RELATIONSHIP_TYPE
} from './types.ts';

// Custom annotation namespace
const SEM_NAMESPACE = 'http://semantical.io/ontology/visual#';
const SKOS_NAMESPACE = 'http://www.w3.org/2004/02/skos/core#';

// =============================================================================
// Internal Types
// =============================================================================

interface RawClassData {
    uri: string;
    localName: string;
    label?: string;
    comment?: string;
    altLabels: string[];
    parentUris: string[];
    classType?: string;
    stereotype?: string;
    icon?: string;
    categoryColor?: string;
}

interface RawPropertyData {
    uri: string;
    localName: string;
    label?: string;
    comment?: string;
    altLabels: string[];
    parentUris: string[];
    type?: string;
    sourceMarker?: string;
    targetMarker?: string;
    strokeStyle?: string;
    categoryColor?: string;
    domain?: ClassExpression;
    range?: ClassExpression;
}

interface RawDataPropertyData {
    uri: string;
    localName: string;
    label?: string;
    range?: string;
    domainClassUris: string[];
}

// =============================================================================
// Annotation Helpers
// =============================================================================

function isIRI(value: unknown): boolean {
    return isFullIRI(value as any) || isAbbreviatedIRI(value as any);
}

function getAnnotationValue(
    onto: Ontology,
    subjectIri: string,
    propertyIri: string
): string | undefined {
    let fallback: string | undefined = undefined;

    for (const axiom of onto.axioms) {
        if (isAnnotationAssertion(axiom)) {
            const subject = axiom.subject;
            if (isIRI(subject) &&
                iriToString(subject as any) === subjectIri &&
                iriToString(axiom.property.iri) === propertyIri) {
                const value = axiom.value;
                if ('lexicalForm' in value) {
                    // Check if this has a language tag
                    if ('languageTag' in value && value.languageTag) {
                        // Prefer English
                        if (value.languageTag === 'en') {
                            return value.lexicalForm;
                        }
                        // Store as fallback if we don't have one yet
                        if (!fallback) {
                            fallback = value.lexicalForm;
                        }
                    } else {
                        // No language tag - return immediately as this is the most generic
                        return value.lexicalForm;
                    }
                }
            }
        }
    }
    return fallback;
}

function getAllAnnotationValues(
    onto: Ontology,
    subjectIri: string,
    propertyIri: string
): string[] {
    const values: string[] = [];
    for (const axiom of onto.axioms) {
        if (isAnnotationAssertion(axiom)) {
            const subject = axiom.subject;
            if (isIRI(subject) &&
                iriToString(subject as any) === subjectIri &&
                iriToString(axiom.property.iri) === propertyIri) {
                const value = axiom.value;
                if ('lexicalForm' in value) {
                    values.push(value.lexicalForm);
                }
            }
        }
    }
    return values;
}

// =============================================================================
// Namespace Helpers
// =============================================================================

function isInNamespace(uri: string, namespaces: string[]): boolean {
    return namespaces.some(ns => uri.startsWith(ns));
}

function getNamespacePrefix(uri: string): string {
    const hashIndex = uri.lastIndexOf('#');
    const slashIndex = uri.lastIndexOf('/');
    const separatorIndex = Math.max(hashIndex, slashIndex);

    if (separatorIndex === -1) return '';

    const namespace = uri.substring(0, separatorIndex);
    const parts = namespace.split('/').filter(p => p.length > 0);
    const lastPart = parts[parts.length - 1] || '';

    return lastPart
        .replace(/-ontology$/, '')
        .replace(/-domain$/, '')
        .replace(/^ns$/, parts[parts.length - 2] || 'ns');
}

// =============================================================================
// Icon Creation
// =============================================================================

function createIconElement(pathData: string, filled: boolean = false): React.ReactNode {
    // Detect if it's a circle path (contains 'A' for arc)
    if (pathData.includes(' A ') || pathData.match(/A\d/)) {
        const match = pathData.match(/M(\d+),(\d+)\s+A(\d+),(\d+)/);
        if (match) {
            const [, cx, cy, r] = match;
            return React.createElement('circle', {
                cx: parseInt(cx),
                cy: parseInt(cy),
                r: parseInt(r),
                fill: filled ? 'currentColor' : 'none',
                stroke: 'currentColor',
                strokeWidth: 1.5
            });
        }
    }

    // Check if path should be filled (closed path ending with Z)
    const shouldFill = filled || (pathData.endsWith('Z') && !pathData.includes('M0,6 L6,6'));

    return React.createElement('path', {
        d: pathData,
        fill: shouldFill ? 'currentColor' : 'none',
        stroke: 'currentColor',
        strokeWidth: 1.5
    });
}

// =============================================================================
// Class Taxonomy Builder
// =============================================================================

function extractClassData(onto: Ontology, allowedNamespaces: string[]): Map<string, RawClassData> {
    const classes = new Map<string, RawClassData>();

    // Find all class declarations
    for (const axiom of onto.axioms) {
        if (isDeclaration(axiom) && isClassEntity(axiom.entity)) {
            const uri = iriToString(axiom.entity.iri);
            if (!isInNamespace(uri, allowedNamespaces)) continue;

            const localName = getLocalName(axiom.entity.iri);

            const data: RawClassData = {
                uri,
                localName,
                label: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'label'),
                comment: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'comment'),
                altLabels: getAllAnnotationValues(onto, uri, SKOS_NAMESPACE + 'altLabel'),
                parentUris: [],
                classType: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'classType'),
                stereotype: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'stereotype'),
                icon: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'icon'),
                categoryColor: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'categoryColor')
            };

            classes.set(uri, data);
        }
    }

    // Add parent URIs from SubClassOf axioms
    for (const axiom of onto.axioms) {
        if (isSubClassOf(axiom) && isClass(axiom.subClass) && isClass(axiom.superClass)) {
            const childUri = iriToString(axiom.subClass.iri);
            const parentUri = iriToString(axiom.superClass.iri);

            const data = classes.get(childUri);
            if (data && !data.parentUris.includes(parentUri)) {
                data.parentUris.push(parentUri);
            }
        }
    }

    return classes;
}

function buildClassTaxonomyTree(
    classes: Map<string, RawClassData>,
    primaryNamespace: string,
    allowedNamespaces: string[],
    prefixes?: Map<string, string>
): ClassTaxonomyNode[] {
    // Build parent-child relationships
    const childrenMap = new Map<string, string[]>();
    const hasNamedParent = new Set<string>();

    for (const [uri, data] of classes) {
        for (const parentUri of data.parentUris) {
            if (isInNamespace(parentUri, allowedNamespaces)) {
                const children = childrenMap.get(parentUri) || [];
                if (!children.includes(uri)) {
                    children.push(uri);
                }
                childrenMap.set(parentUri, children);
                hasNamedParent.add(uri);
            }
        }
    }

    // Root classes are those without named parent
    const rootUris: string[] = [];
    for (const [uri] of classes) {
        if (!hasNamedParent.has(uri)) {
            rootUris.push(uri);
        }
    }

    // Recursive function to build node
    const buildNode = (uri: string): ClassTaxonomyNode | null => {
        const data = classes.get(uri);
        if (!data) return null;

        const childUris = childrenMap.get(uri) || [];
        const children = childUris
            .map(childUri => buildNode(childUri))
            .filter((node): node is ClassTaxonomyNode => node !== null);

        // Sort children: categories first, then by label
        children.sort((a, b) => {
            const aIsCategory = !a.classType;
            const bIsCategory = !b.classType;
            if (aIsCategory && !bIsCategory) return -1;
            if (!aIsCategory && bIsCategory) return 1;
            return (a.label || '').localeCompare(b.label || '');
        });

        const isImported = !data.uri.startsWith(primaryNamespace);
        let namespacePrefix: string | undefined = undefined;

        if (prefixes) {
            // Extract namespace from URI (before # or last /)
            const hashIndex = data.uri.lastIndexOf('#');
            const slashIndex = data.uri.lastIndexOf('/');
            const separatorIndex = Math.max(hashIndex, slashIndex);

            if (separatorIndex !== -1) {
                const namespace = data.uri.substring(0, separatorIndex + 1);
                namespacePrefix = prefixes.get(namespace);
            }
        }

        // Fallback to URI-derived prefix if not found in workspace prefixes
        if (!namespacePrefix) {
            namespacePrefix = getNamespacePrefix(data.uri);
        }

        const node: ClassTaxonomyNode = {
            id: data.uri,  // Use full URI for unique ID (localName can collide across ontologies)
            uri: data.uri,
            label: data.label || data.localName,
            description: data.comment,
            keywords: data.altLabels.length > 0 ? data.altLabels : undefined,
            categoryColor: data.categoryColor,
            classType: data.classType || data.localName.toLowerCase(),
            stereotype: data.stereotype,
            isImported,
            namespacePrefix
        };

        if (data.icon) {
            node.icon = createIconElement(data.icon);
        }

        if (children.length > 0) {
            node.children = children;
        }

        return node;
    };

    return rootUris
        .map(uri => buildNode(uri))
        .filter((node): node is ClassTaxonomyNode => node !== null)
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

/**
 * Build class taxonomy from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 *
 * @param onto - The parsed Ontology object
 * @param primaryNamespace - The primary namespace (used for isImported flag)
 * @param allowedNamespaces - Namespaces to include (defaults to primaryNamespace only)
 * @param prefixes - Map of namespace URIs to prefix names (for imported classes)
 */
export function buildClassTaxonomyFromOntology(
    onto: Ontology,
    primaryNamespace: string,
    allowedNamespaces?: string[],
    prefixes?: Map<string, string>
): ClassTaxonomyNode[] {
    const namespaces = allowedNamespaces || [primaryNamespace];
    const classes = extractClassData(onto, namespaces);
    return buildClassTaxonomyTree(classes, primaryNamespace, namespaces, prefixes);
}

/**
 * Build class taxonomy from multiple parsed Ontology objects.
 * This is the correct way to handle cross-ontology inheritance.
 *
 * Instead of building separate trees per ontology and concatenating them,
 * this function extracts ALL classes from ALL ontologies first, then builds
 * ONE unified tree. This ensures that cross-ontology parent-child relationships
 * work correctly (e.g., dprod:DataProduct subClassOf dcat:Resource).
 *
 * @param ontologies - Array of parsed Ontology objects
 * @param primaryNamespace - The primary namespace (used for isImported flag)
 * @param allowedNamespaces - Namespaces to include (defaults to primaryNamespace only)
 * @param prefixes - Map of namespace URIs to prefix names (for imported classes)
 */
export function buildClassTaxonomyFromMultipleOntologies(
    ontologies: Ontology[],
    primaryNamespace: string,
    allowedNamespaces?: string[],
    prefixes?: Map<string, string>
): ClassTaxonomyNode[] {
    const namespaces = allowedNamespaces || [primaryNamespace];

    // Step 1: Extract ALL classes from ALL ontologies into a single merged map
    const mergedClasses = new Map<string, RawClassData>();

    for (const onto of ontologies) {
        const classes = extractClassData(onto, namespaces);

        // Merge into the combined map (later ontologies override earlier ones if there are duplicates)
        for (const [uri, data] of classes) {
            mergedClasses.set(uri, data);
        }
    }

    // Step 2: Build ONE unified tree from the merged classes map
    return buildClassTaxonomyTree(mergedClasses, primaryNamespace, namespaces, prefixes);
}

/**
 * Build class taxonomy from TTL content (parses TTL first).
 * @deprecated Prefer buildClassTaxonomyFromOntology with OntologySchema.ontology
 */
export async function buildClassTaxonomy(
    turtleContent: string,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Promise<ClassTaxonomyNode[]> {
    const onto = await parseTurtle(turtleContent);
    return buildClassTaxonomyFromOntology(onto, primaryNamespace, allowedNamespaces);
}

// =============================================================================
// Class Hierarchy Builder
// =============================================================================

/**
 * Build class hierarchy from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 */
export function buildClassHierarchyFromOntology(onto: Ontology): ClassHierarchyMap {
    const hierarchy = new Map<string, string[]>();

    for (const axiom of onto.axioms) {
        if (isSubClassOf(axiom) && isClass(axiom.subClass) && isClass(axiom.superClass)) {
            const childUri = iriToString(axiom.subClass.iri);
            const parentUri = iriToString(axiom.superClass.iri);

            const parents = hierarchy.get(childUri) || [];
            if (!parents.includes(parentUri)) {
                parents.push(parentUri);
            }
            hierarchy.set(childUri, parents);
        }
    }

    return hierarchy;
}

/**
 * Build class hierarchy from TTL content (parses TTL first).
 * @deprecated Prefer buildClassHierarchyFromOntology with OntologySchema.ontology
 */
export async function buildClassHierarchy(turtleContent: string): Promise<ClassHierarchyMap> {
    const onto = await parseTurtle(turtleContent);
    return buildClassHierarchyFromOntology(onto);
}

// =============================================================================
// Relationship Taxonomy Builder
// =============================================================================

function getPropertyUri(prop: ObjectPropertyExpression): string | undefined {
    if (isObjectProperty(prop)) {
        return iriToString(prop.iri);
    }
    return undefined;
}

function extractPropertyData(onto: Ontology, allowedNamespaces: string[]): Map<string, RawPropertyData> {
    const properties = new Map<string, RawPropertyData>();

    // Find all object property declarations
    for (const axiom of onto.axioms) {
        if (isDeclaration(axiom) && isObjectPropertyEntity(axiom.entity)) {
            const uri = iriToString(axiom.entity.iri);
            if (!isInNamespace(uri, allowedNamespaces)) continue;

            const localName = getLocalName(axiom.entity.iri);

            const data: RawPropertyData = {
                uri,
                localName,
                label: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'label'),
                comment: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'comment'),
                altLabels: getAllAnnotationValues(onto, uri, SKOS_NAMESPACE + 'altLabel'),
                parentUris: [],
                // Try sem:relationshipType first (used in PartyDomainVisual.ttl), fallback to sem:type
                type: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'relationshipType')
                    || getAnnotationValue(onto, uri, SEM_NAMESPACE + 'type'),
                sourceMarker: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'sourceMarker'),
                targetMarker: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'targetMarker'),
                strokeStyle: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'strokeStyle'),
                categoryColor: getAnnotationValue(onto, uri, SEM_NAMESPACE + 'categoryColor')
            };

            properties.set(uri, data);
        }
    }

    // Add parent URIs from SubObjectPropertyOf axioms
    for (const axiom of onto.axioms) {
        if (isSubObjectPropertyOf(axiom)) {
            const subPropUri = getPropertyUri(axiom.subProperty as any);
            const superPropUri = getPropertyUri(axiom.superProperty);
            if (subPropUri && superPropUri) {
                const data = properties.get(subPropUri);
                if (data && !data.parentUris.includes(superPropUri)) {
                    data.parentUris.push(superPropUri);
                }
            }
        }
    }

    // Add domain/range from ObjectPropertyDomain/ObjectPropertyRange axioms
    for (const axiom of onto.axioms) {
        if (isObjectPropertyDomain(axiom)) {
            const propUri = getPropertyUri(axiom.property);
            if (propUri) {
                const data = properties.get(propUri);
                if (data) {
                    data.domain = axiom.domain;
                }
            }
        }
        if (isObjectPropertyRange(axiom)) {
            const propUri = getPropertyUri(axiom.property);
            if (propUri) {
                const data = properties.get(propUri);
                if (data) {
                    data.range = axiom.range;
                }
            }
        }
    }

    return properties;
}

function buildRelationshipTaxonomyTree(
    properties: Map<string, RawPropertyData>,
    allowedNamespaces: string[]
): RelationshipTaxonomyNode[] {
    // Build parent-child relationships
    const childrenMap = new Map<string, string[]>();
    const hasNamedParent = new Set<string>();

    for (const [uri, data] of properties) {
        for (const parentUri of data.parentUris) {
            if (isInNamespace(parentUri, allowedNamespaces)) {
                const children = childrenMap.get(parentUri) || [];
                if (!children.includes(uri)) {
                    children.push(uri);
                }
                childrenMap.set(parentUri, children);
                hasNamedParent.add(uri);
            }
        }
    }

    // Root properties are those without named parent
    const rootUris: string[] = [];
    for (const [uri] of properties) {
        if (!hasNamedParent.has(uri)) {
            rootUris.push(uri);
        }
    }

    // Recursive function to build node
    const buildNode = (uri: string): RelationshipTaxonomyNode | null => {
        const data = properties.get(uri);
        if (!data) return null;

        const childUris = childrenMap.get(uri) || [];
        const children = childUris
            .map(childUri => buildNode(childUri))
            .filter((node): node is RelationshipTaxonomyNode => node !== null);

        // Sort children by label
        children.sort((a, b) => (a.label || '').localeCompare(b.label || ''));

        const node: RelationshipTaxonomyNode = {
            id: data.uri,  // Use full URI for unique ID (localName can collide across ontologies)
            uri: data.uri,
            label: data.label || data.localName,
            description: data.comment,
            keywords: data.altLabels.length > 0 ? data.altLabels : undefined,
            categoryColor: data.categoryColor,
            type: (data.type || data.localName.toLowerCase()) as RELATIONSHIP_TYPE,
            sourceMarker: data.sourceMarker,
            targetMarker: data.targetMarker,
            strokeStyle: data.strokeStyle,
            domain: data.domain,
            range: data.range
        };

        if (children.length > 0) {
            node.children = children;
        }

        return node;
    };

    return rootUris
        .map(uri => buildNode(uri))
        .filter((node): node is RelationshipTaxonomyNode => node !== null)
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

/**
 * Build relationship taxonomy from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 */
export function buildRelationshipTaxonomyFromOntology(
    onto: Ontology,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): RelationshipTaxonomyNode[] {
    const namespaces = allowedNamespaces || [primaryNamespace];
    const properties = extractPropertyData(onto, namespaces);
    return buildRelationshipTaxonomyTree(properties, namespaces);
}

/**
 * Build relationship taxonomy from TTL content (parses TTL first).
 * @deprecated Prefer buildRelationshipTaxonomyFromOntology with OntologySchema.ontology
 */
export async function buildRelationshipTaxonomy(
    turtleContent: string,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Promise<RelationshipTaxonomyNode[]> {
    const onto = await parseTurtle(turtleContent);
    return buildRelationshipTaxonomyFromOntology(onto, primaryNamespace, allowedNamespaces);
}

// =============================================================================
// Data Properties Builder
// =============================================================================

export type ClassDataPropertiesMap = Map<string, DataProperty[]>;

function extractDataPropertyData(onto: Ontology, allowedNamespaces: string[]): Map<string, RawDataPropertyData> {
    const dataProps = new Map<string, RawDataPropertyData>();

    // Find all data property declarations
    for (const axiom of onto.axioms) {
        if (isDeclaration(axiom) && isDataPropertyEntity(axiom.entity)) {
            const uri = iriToString(axiom.entity.iri);
            if (!isInNamespace(uri, allowedNamespaces)) continue;

            const localName = getLocalName(axiom.entity.iri);

            const data: RawDataPropertyData = {
                uri,
                localName,
                label: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'label'),
                domainClassUris: []
            };

            dataProps.set(uri, data);
        }
    }

    // Add domain from DataPropertyDomain axioms
    for (const axiom of onto.axioms) {
        if (isDataPropertyDomain(axiom)) {
            const propUri = iriToString(axiom.property.iri);
            const data = dataProps.get(propUri);
            if (data && isClass(axiom.domain)) {
                const domainUri = iriToString(axiom.domain.iri);
                if (!data.domainClassUris.includes(domainUri)) {
                    data.domainClassUris.push(domainUri);
                }
            }
        }
    }

    // Add range from DataPropertyRange axioms
    for (const axiom of onto.axioms) {
        if (isDataPropertyRange(axiom)) {
            const propUri = iriToString(axiom.property.iri);
            const data = dataProps.get(propUri);
            if (data && 'iri' in axiom.range) {
                data.range = iriToString(axiom.range.iri);
            }
        }
    }

    return dataProps;
}

/**
 * Build data properties map from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 */
export function buildDataPropertiesMapFromOntology(
    onto: Ontology,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): ClassDataPropertiesMap {
    const namespaces = allowedNamespaces || [primaryNamespace];
    const dataProps = extractDataPropertyData(onto, namespaces);

    // Build class hierarchy for inheritance lookup
    const classHierarchy = new Map<string, string[]>(); // child -> parents
    const allClasses = new Set<string>();

    // Collect data property restrictions from SubClassOf axioms
    // Map: classUri -> propUri -> { min, max, range }
    const directRestrictions = new Map<string, Map<string, { min: number; max: number | undefined; range?: string }>>();

    for (const axiom of onto.axioms) {
        // Collect declared classes
        if (isDeclaration(axiom) && isClassEntity(axiom.entity)) {
            allClasses.add(iriToString(axiom.entity.iri));
        }

        // Build hierarchy and collect data property restrictions
        if (isSubClassOf(axiom) && isClass(axiom.subClass)) {
            const classUri = iriToString(axiom.subClass.iri);

            if (isClass(axiom.superClass)) {
                // Named class parent - record for hierarchy
                const parentUri = iriToString(axiom.superClass.iri);
                const parents = classHierarchy.get(classUri) || [];
                if (!parents.includes(parentUri)) {
                    parents.push(parentUri);
                }
                classHierarchy.set(classUri, parents);
            } else {
                // Check for data property restrictions
                const superClass = axiom.superClass;
                let propUri: string | undefined;
                let min = 0;
                let max: number | undefined = undefined;
                let range: string | undefined;

                if (isDataSomeValuesFrom(superClass)) {
                    propUri = iriToString(superClass.properties[0].iri);
                    min = 1;
                    if ('iri' in superClass.dataRange) {
                        range = iriToString(superClass.dataRange.iri);
                    }
                } else if (isDataAllValuesFrom(superClass)) {
                    propUri = iriToString(superClass.properties[0].iri);
                    if ('iri' in superClass.dataRange) {
                        range = iriToString(superClass.dataRange.iri);
                    }
                } else if (isDataMinCardinality(superClass)) {
                    propUri = iriToString(superClass.property.iri);
                    min = superClass.cardinality;
                    if (superClass.dataRange && 'iri' in superClass.dataRange) {
                        range = iriToString(superClass.dataRange.iri);
                    }
                } else if (isDataMaxCardinality(superClass)) {
                    propUri = iriToString(superClass.property.iri);
                    max = superClass.cardinality;
                    if (superClass.dataRange && 'iri' in superClass.dataRange) {
                        range = iriToString(superClass.dataRange.iri);
                    }
                } else if (isDataExactCardinality(superClass)) {
                    propUri = iriToString(superClass.property.iri);
                    min = superClass.cardinality;
                    max = superClass.cardinality;
                    if (superClass.dataRange && 'iri' in superClass.dataRange) {
                        range = iriToString(superClass.dataRange.iri);
                    }
                }

                if (propUri) {
                    const classRestrictions = directRestrictions.get(classUri) || new Map();
                    const existing = classRestrictions.get(propUri) || { min: 0, max: undefined, range: undefined };
                    existing.min = Math.max(existing.min, min);
                    if (max !== undefined) {
                        existing.max = existing.max !== undefined ? Math.min(existing.max, max) : max;
                    }
                    if (range) existing.range = range;
                    classRestrictions.set(propUri, existing);
                    directRestrictions.set(classUri, classRestrictions);
                }
            }
        }
    }

    // Helper to check if a class is a subclass of target (transitive)
    const isSubclassOfTransitive = (classUri: string, targetUri: string, visited: Set<string> = new Set()): boolean => {
        if (classUri === targetUri) return true;
        if (visited.has(classUri)) return false;
        visited.add(classUri);

        const parents = classHierarchy.get(classUri) || [];
        for (const parentUri of parents) {
            if (isSubclassOfTransitive(parentUri, targetUri, visited)) return true;
        }
        return false;
    };

    // Helper to get inherited restrictions for a class
    const getInheritedRestrictions = (classUri: string, visited: Set<string> = new Set()): Map<string, { min: number; max: number | undefined; range?: string }> => {
        if (visited.has(classUri)) return new Map();
        visited.add(classUri);

        const result = new Map<string, { min: number; max: number | undefined; range?: string }>();

        const direct = directRestrictions.get(classUri);
        if (direct) {
            for (const [propUri, restriction] of direct) {
                result.set(propUri, { ...restriction });
            }
        }

        const parents = classHierarchy.get(classUri) || [];
        for (const parentUri of parents) {
            const parentRestrictions = getInheritedRestrictions(parentUri, visited);
            for (const [propUri, restriction] of parentRestrictions) {
                const existing = result.get(propUri);
                if (existing) {
                    existing.min = Math.max(existing.min, restriction.min);
                    if (restriction.max !== undefined) {
                        existing.max = existing.max !== undefined ? Math.min(existing.max, restriction.max) : restriction.max;
                    }
                    if (restriction.range) existing.range = restriction.range;
                } else {
                    result.set(propUri, { ...restriction });
                }
            }
        }

        return result;
    };

    // Helper to format datatype range (strip namespace)
    const formatRange = (rangeUri: string | undefined): string => {
        if (!rangeUri) return 'string';
        const hashIndex = rangeUri.lastIndexOf('#');
        if (hashIndex !== -1) return rangeUri.substring(hashIndex + 1);
        const slashIndex = rangeUri.lastIndexOf('/');
        if (slashIndex !== -1) return rangeUri.substring(slashIndex + 1);
        return rangeUri;
    };

    // Build map from class URI to data properties
    const result: ClassDataPropertiesMap = new Map();

    for (const classUri of allClasses) {
        if (!isInNamespace(classUri, namespaces)) continue;

        const classDataProps: DataProperty[] = [];
        const addedPropUris = new Set<string>();

        const inheritedRestrictions = getInheritedRestrictions(classUri);
        for (const [propUri, restriction] of inheritedRestrictions) {
            const propData = dataProps.get(propUri);
            const localName = propData?.localName || propUri.substring(propUri.lastIndexOf('#') + 1);
            const label = propData?.label || localName;
            const range = formatRange(restriction.range || propData?.range);

            classDataProps.push({
                uri: propUri,
                localName,
                label,
                range,
                cardinality: {
                    min: restriction.min,
                    max: restriction.max
                }
            });
            addedPropUris.add(propUri);
        }

        for (const [, data] of dataProps) {
            if (addedPropUris.has(data.uri)) continue;

            for (const domainClassUri of data.domainClassUris) {
                if (isSubclassOfTransitive(classUri, domainClassUri)) {
                    classDataProps.push({
                        uri: data.uri,
                        localName: data.localName,
                        label: data.label || data.localName,
                        range: formatRange(data.range)
                    });
                    addedPropUris.add(data.uri);
                    break;
                }
            }
        }

        if (classDataProps.length > 0) {
            result.set(classUri, classDataProps);
        }
    }

    return result;
}

/**
 * Build data properties map from TTL content (parses TTL first).
 * @deprecated Prefer buildDataPropertiesMapFromOntology with OntologySchema.ontology
 */
export async function buildDataPropertiesMap(
    turtleContent: string,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Promise<ClassDataPropertiesMap> {
    const onto = await parseTurtle(turtleContent);
    return buildDataPropertiesMapFromOntology(onto, primaryNamespace, allowedNamespaces);
}

// =============================================================================
// SubClassOf Axioms Map Builder
// =============================================================================

export type ClassSubClassOfAxiomsMap = Map<string, ClassExpression[]>;
export type ClassObjectPropertyRestrictionsMap = ClassSubClassOfAxiomsMap;

/**
 * Build SubClassOf axioms map from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 */
export function buildSubClassOfAxiomsMapFromOntology(
    onto: Ontology,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): ClassSubClassOfAxiomsMap {
    const namespaces = allowedNamespaces || [primaryNamespace];

    const directRestrictions: ClassSubClassOfAxiomsMap = new Map();
    const classHierarchy = new Map<string, string[]>();
    const allClasses = new Set<string>();

    for (const axiom of onto.axioms) {
        if (isDeclaration(axiom) && isClassEntity(axiom.entity)) {
            allClasses.add(iriToString(axiom.entity.iri));
        }

        if (isSubClassOf(axiom) && isClass(axiom.subClass)) {
            const classUri = iriToString(axiom.subClass.iri);

            if (isClass(axiom.superClass)) {
                const parentUri = iriToString(axiom.superClass.iri);
                const parents = classHierarchy.get(classUri) || [];
                if (!parents.includes(parentUri)) {
                    parents.push(parentUri);
                }
                classHierarchy.set(classUri, parents);
            } else {
                const existing = directRestrictions.get(classUri) || [];
                existing.push(axiom.superClass);
                directRestrictions.set(classUri, existing);
            }
        }
    }

    const isSubclassOfTransitive = (classUri: string, targetUri: string, visited: Set<string> = new Set()): boolean => {
        if (classUri === targetUri) return true;
        if (visited.has(classUri)) return false;
        visited.add(classUri);

        const parents = classHierarchy.get(classUri) || [];
        for (const parentUri of parents) {
            if (isSubclassOfTransitive(parentUri, targetUri, visited)) return true;
        }
        return false;
    };

    const classSatisfiesDomain = (classUri: string, domain: ClassExpression): boolean => {
        switch (domain.type) {
            case 'Class':
                return isSubclassOfTransitive(classUri, iriToString(domain.iri));
            case 'ObjectUnionOf':
                return domain.classExpressions.some(expr => classSatisfiesDomain(classUri, expr));
            case 'ObjectIntersectionOf':
                return domain.classExpressions.every(expr => classSatisfiesDomain(classUri, expr));
            default:
                return true;
        }
    };

    const propertyDomainRange = new Map<string, { domain?: ClassExpression; range?: ClassExpression }>();

    for (const axiom of onto.axioms) {
        if (isObjectPropertyDomain(axiom)) {
            const propUri = getPropertyUri(axiom.property);
            if (propUri) {
                const existing = propertyDomainRange.get(propUri) || {};
                existing.domain = axiom.domain;
                propertyDomainRange.set(propUri, existing);
            }
        }
        if (isObjectPropertyRange(axiom)) {
            const propUri = getPropertyUri(axiom.property);
            if (propUri) {
                const existing = propertyDomainRange.get(propUri) || {};
                existing.range = axiom.range;
                propertyDomainRange.set(propUri, existing);
            }
        }
    }

    const result: ClassSubClassOfAxiomsMap = new Map();

    const getInheritedRestrictions = (classUri: string, visited: Set<string> = new Set()): ClassExpression[] => {
        if (visited.has(classUri)) return [];
        visited.add(classUri);

        const restrictions = [...(directRestrictions.get(classUri) || [])];

        const parents = classHierarchy.get(classUri) || [];
        for (const parentUri of parents) {
            const parentRestrictions = getInheritedRestrictions(parentUri, visited);
            restrictions.push(...parentRestrictions);
        }

        return restrictions;
    };

    const createDomainBasedRestrictions = (classUri: string): ClassExpression[] => {
        const restrictions: ClassExpression[] = [];
        const existingPropertyUris = new Set<string>();

        const explicitRestrictions = getInheritedRestrictions(classUri);
        for (const r of explicitRestrictions) {
            if ('property' in r && r.property) {
                const propUri = getPropertyUri(r.property as any);
                if (propUri) existingPropertyUris.add(propUri);
            }
        }

        for (const [propUri, { domain, range }] of propertyDomainRange) {
            if (existingPropertyUris.has(propUri)) continue;

            if (domain && classSatisfiesDomain(classUri, domain)) {
                const filler: ClassExpression = range && isClass(range)
                    ? range
                    : owlClass(fullIRI('http://www.w3.org/2002/07/owl#Thing'));

                const propExpression: ObjectPropertyExpression = {
                    type: 'ObjectProperty',
                    iri: fullIRI(propUri)
                };

                const syntheticRestriction = objectAllValuesFrom(propExpression, filler);
                restrictions.push(syntheticRestriction);
            }
        }

        return restrictions;
    };

    for (const classUri of allClasses) {
        if (!isInNamespace(classUri, namespaces)) continue;

        const inheritedRestrictions = getInheritedRestrictions(classUri);
        const domainBasedRestrictions = createDomainBasedRestrictions(classUri);
        const allRestrictions = [...inheritedRestrictions, ...domainBasedRestrictions];

        if (allRestrictions.length > 0) {
            result.set(classUri, allRestrictions);
        }
    }

    return result;
}

/**
 * Build SubClassOf axioms map from TTL content (parses TTL first).
 * @deprecated Prefer buildSubClassOfAxiomsMapFromOntology with OntologySchema.ontology
 */
export async function buildSubClassOfAxiomsMap(
    turtleContent: string,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Promise<ClassSubClassOfAxiomsMap> {
    const onto = await parseTurtle(turtleContent);
    return buildSubClassOfAxiomsMapFromOntology(onto, primaryNamespace, allowedNamespaces);
}

// =============================================================================
// isSubclassOf Helper
// =============================================================================

export function isSubclassOf(
    classUri: string,
    targetUri: string,
    hierarchy: ClassHierarchyMap
): boolean {
    if (classUri === targetUri) return true;

    const visited = new Set<string>();
    const checkAncestors = (uri: string): boolean => {
        if (visited.has(uri)) return false;
        visited.add(uri);

        const parents = hierarchy.get(uri);
        if (!parents) return false;

        for (const parentUri of parents) {
            if (parentUri === targetUri) return true;
            if (checkAncestors(parentUri)) return true;
        }
        return false;
    };

    return checkAncestors(classUri);
}

// =============================================================================
// Schema Individuals Builder (TBox-level individuals)
// =============================================================================

/**
 * Build a map of schema individuals from a parsed Ontology object.
 * Use this when you already have a parsed Ontology (e.g., from OntologySchema.ontology).
 *
 * These are owl:NamedIndividual declarations used for:
 * - Enumerations (owl:oneOf members)
 * - Value constraints (owl:hasValue targets)
 * - Controlled lists (predefined allowed values)
 *
 * @param onto - The parsed Ontology object
 * @param primaryNamespace - The primary namespace to filter individuals
 * @param allowedNamespaces - Optional additional namespaces to include
 * @returns Map of individual URI to SchemaIndividual
 */
export function buildSchemaIndividualsFromOntology(
    onto: Ontology,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Map<string, SchemaIndividual> {
    const namespaces = allowedNamespaces || [primaryNamespace];
    const individuals = new Map<string, SchemaIndividual>();

    // Temporary storage for individual data
    const individualData = new Map<string, {
        uri: string;
        label?: string;
        comment?: string;
        classUris: string[];
    }>();

    // Find all named individual declarations
    for (const axiom of onto.axioms) {
        if (isDeclaration(axiom) && isNamedIndividualEntity(axiom.entity)) {
            const uri = iriToString(axiom.entity.iri);
            if (!isInNamespace(uri, namespaces)) continue;

            individualData.set(uri, {
                uri,
                label: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'label'),
                comment: getAnnotationValue(onto, uri, RDFS_NAMESPACE + 'comment'),
                classUris: []
            });
        }
    }

    // Collect class assertions (rdf:type) for each individual
    for (const axiom of onto.axioms) {
        if (isClassAssertion(axiom) && isNamedIndividual(axiom.individual)) {
            const individualUri = iriToString(axiom.individual.iri);
            const data = individualData.get(individualUri);

            if (data && isClass(axiom.classExpression)) {
                const classUri = iriToString(axiom.classExpression.iri);
                if (!data.classUris.includes(classUri)) {
                    data.classUris.push(classUri);
                }
            }
        }
    }

    // Create SchemaIndividual instances
    for (const [uri, data] of individualData) {
        const label = data.label || getLocalName(fullIRI(uri));
        const individual = new SchemaIndividual(
            uri,
            label,
            data.classUris,
            data.comment || ''
        );
        individuals.set(uri, individual);
    }

    return individuals;
}

/**
 * Build a map of schema individuals (TBox-level named individuals) from turtle content.
 * @deprecated Prefer buildSchemaIndividualsFromOntology with OntologySchema.ontology
 */
export async function buildSchemaIndividuals(
    turtleContent: string,
    primaryNamespace: string,
    allowedNamespaces?: string[]
): Promise<Map<string, SchemaIndividual>> {
    const onto = await parseTurtle(turtleContent);
    return buildSchemaIndividualsFromOntology(onto, primaryNamespace, allowedNamespaces);
}

// =============================================================================
// Ontology Metadata Extractor
// =============================================================================

/**
 * Prefix declaration mapping a prefix name to a namespace IRI.
 */
export interface PrefixEntry {
    /** The prefix name (without the colon) */
    prefix: string;
    /** The namespace IRI */
    namespace: string;
}

/**
 * Ontology-level metadata extracted from Turtle content.
 * Used to populate OntologySchema with OWL2 ontology identifiers and imports.
 */
export interface OntologyMetadata {
    /** The ontology IRI (OWL2 ontologyIRI) */
    ontologyIRI?: string;
    /** The version IRI (OWL2 versionIRI) */
    versionIRI?: string;
    /** IRIs of imported ontologies (OWL2 owl:imports) */
    imports: string[];
    /** Prefix declarations from the ontology */
    prefixes: PrefixEntry[];
}

/**
 * Extract ontology-level metadata from Turtle content.
 *
 * Parses the Turtle content and extracts the ontology IRI, version IRI,
 * import declarations, and prefix declarations. This metadata is used to
 * populate OntologySchema instances with OWL2-compliant identifiers.
 *
 * @param turtleContent - The Turtle content to parse
 * @returns OntologyMetadata containing ontologyIRI, versionIRI, imports, and prefixes
 */
export async function extractOntologyMetadata(turtleContent: string): Promise<OntologyMetadata> {
    const onto = await parseTurtle(turtleContent);

    return {
        ontologyIRI: onto.ontologyIRI ? iriToString(onto.ontologyIRI) : undefined,
        versionIRI: onto.versionIRI ? iriToString(onto.versionIRI) : undefined,
        imports: onto.imports.map(imp => iriToString(imp.iri)),
        prefixes: onto.prefixes.map(p => ({ prefix: p.name, namespace: p.iri }))
    };
}
